import { useMemo } from "react";
import { TaskType } from "@common/types/task";
import { BillViewSchema } from "@common/types/views/billViews";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { getBillsTaskFilterTabs } from "@components/tasks/TaskBody/Bills/billsTaskFilters";
import {
    defaultBillsTaskView,
    parseBillsTaskView,
    serializeBillsTemplateView,
} from "@components/tasks/TaskBody/Bills/billsTaskView";
import { useBillsTaskViewConfig } from "@components/tasks/TaskBody/Bills/useBillsTaskViewConfig";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { isBillProviderAccount } from "@helpers/bills";
import { useScope } from "@state/scopeContext.lib";
import styles from "./CloseTemplateTaskTransactionsPreview.module.scss";
import { TemplateBillsDataTable } from "./dataPreview/TemplateBillsDataTable";
import { TemplatePreviewSwitcher } from "./dataPreview/TemplatePreviewSwitcher";
import { useTemplateTaskPreview } from "./dataPreview/useTemplateTaskPreview";
import type { TemplateTaskPreviewProps } from "./templateTaskDrawer/templateTaskPreviewComponents";
import { useCloseTemplateTaskViewState } from "./useCloseTemplateTaskViewState";

/**
 * Right-panel content for the Add/Edit Close Template Task Drawer when the
 * task type is BILLS.
 *
 * Shows a read-only bills table with the rows the task would show for the
 * previewed client and entity (see `dataPreview/`), with column management and
 * filters. Without a workspace (org-level, non client specific template) only
 * workspace-agnostic filters are offered; a workspace-scoped template offers
 * every bill filter. The date range and entity are not configurable here:
 * apply injects the close period and the entity receiving the task.
 */
export const CloseTemplateTaskBillsPreview: React.FC<
    TemplateTaskPreviewProps
> = ({ viewConfig, onViewConfigChange }) => {
    const { workspaceId } = useScope();
    const { view, handleViewChange, handleFiltersChange } =
        useCloseTemplateTaskViewState({
            viewConfig,
            onViewConfigChange,
            parseViewConfig: parseBillsTaskView,
            serializeView: serializeBillsTemplateView,
            viewSchema: BillViewSchema,
        });
    const { viewConfig: billsViewConfig } = useBillsTaskViewConfig();

    const defaultView = useMemo(() => defaultBillsTaskView(), []);
    const availableFilters = useMemo(
        () => getBillsTaskFilterTabs({ isWorkspaceScoped: !!workspaceId }),
        [workspaceId],
    );

    const { previewTarget, resolvedView } = useTemplateTaskPreview({
        taskType: TaskType.BILLS,
        viewConfig: viewConfig ?? serializeBillsTemplateView(view),
    });

    return (
        <div>
            <TablePageHeader
                className={styles.filters}
                filters={view.filters}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                accountFilterPredicate={isBillProviderAccount}
                counterpartyEntityLabel="Vendor"
                hideClearButton
                isFullPage
            >
                <ColumnSettings
                    view={view}
                    viewConfig={billsViewConfig}
                    defaultView={defaultView}
                    onViewChange={handleViewChange}
                />
                <TemplatePreviewSwitcher target={previewTarget} />
            </TablePageHeader>

            <TemplateBillsDataTable
                templateView={view}
                onTemplateViewChange={handleViewChange}
                resolvedView={resolvedView}
                workspaceId={previewTarget.workspaceId}
            />
        </div>
    );
};
